import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '../Button/Button';
import { Text } from '../Text/Text';
import { Container } from '../Container/Container';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-bg-secondary p-bg-v2-xl">
          <Container padding="4xl" className="max-w-md w-full flex flex-col gap-bg-v2-lg items-center text-center rounded-bg-v2-md container-shadow-lg bg-bg-primary">
            <div className="w-16 h-16 rounded-full bg-bg-error-secondary flex items-center justify-center mb-bg-v2-sm">
              <span className="text-3xl">⚠️</span>
            </div>
            
            <Text variant="display-xs" weight="semibold" color="primary">
              Something went wrong
            </Text>
            
            <Text variant="text-md" color="secondary" className="mb-bg-v2-md">
              An unexpected error occurred in the application. We've been notified and are looking into it.
            </Text>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="w-full p-bg-v2-md bg-bg-secondary-alt rounded-bg-v2-sm text-left overflow-auto max-h-40 mb-bg-v2-md">
                <Text variant="text-xs" className="font-mono text-text-error-primary">
                  {this.state.error.toString()}
                </Text>
              </div>
            )}

            <Button 
              variant="primary" 
              onClick={this.handleReset}
              label="Refresh Page"
              className="w-full"
            />
          </Container>
        </div>
      );
    }

    return this.props.children;
  }
}
